import { format } from 'date-fns';
import { FC, ReactNode, useEffect, useState } from 'react';
import { twMerge } from 'tailwind-merge';

import { CrossIcon, EditIcon } from '../../../../assets/icons/index';
import Button from '../../../../components/button/index';
import { Flag, FlagCode } from '../../../../components/flag/index';
import Loader from '../../../../components/loader/index';
import { SummaryList } from '../../../../components/summary-list/index';
import { TextEditor } from '../../../../components/text-editor/index';
import { Tooltip } from '../../../../components/tooltip/index';
import {
  countryIso2TranslationKey,
  RegionGroup,
  regionTranslationKey,
} from '../../../../utils/check-region-group.util';
import { getS3FileOriginalName } from '../../../../utils/get-s3file-original-name.util';
import { companyIndustryTranslation } from '../../../companies/view-company-modal/view-company-modal.types';
import { AddPerkPayload, useAddPerk } from '../hooks/use-add-perk.hook';
import { usePatchPerk } from '../hooks/use-patch-perk.hook';
import { usePerk } from '../hooks/use-perk.hook';
import { DraftPayload, useSaveDraft } from '../hooks/use-save-draft.hook';
import {
  PerkClaimType,
  perkClaimTypeTranslationKey,
  perkOfferTranslationKey,
  RegionType,
} from '../perks.types';
import { formStore, PerkFormMode, PerkFormVariant } from './form.store';
import { PerkFormSchema } from './form.validation';
import { StepFive } from './steps/step-five';
import { StepFour } from './steps/step-four';
import { StepOne } from './steps/step-one';
import { StepThree } from './steps/step-three';
import { StepTwo } from './steps/step-two';
import { PerkStepFormProps } from './steps/steps.types';

const fromStepComponents: Tuple<(data: PerkStepFormProps) => ReactNode, 5> = [
  (payload) => <StepOne {...payload} />,
  (payload) => <StepTwo {...payload} />,
  (payload) => <StepThree {...payload} />,
  (payload) => <StepFour {...payload} />,
  (payload) => <StepFive {...payload} />,
];

const perksFormModeTranslationKey: Record<PerkFormMode, string> = {
  [PerkFormMode.EDIT]: 'Edit Perk',
  [PerkFormMode.VIEW]: 'View Perk',
  [PerkFormMode.CREATE]: 'Create Perk',
};

export const PerkForm: FC = () => {
  const [draftFormPayload, setDraftFormPayload] = useState<DeepPartial<PerkFormSchema>>({});
  const [formPayload, setFormPayload] = useState<DeepPartial<PerkFormSchema>>({});

  const step = formStore(({ step }) => step);
  const [currentStep, setCurrentStep] = useState(step);

  useEffect(() => {
    setCurrentStep(step);
  }, [step, setCurrentStep]);

  const mode = formStore(({ formMode }) => formMode);
  const variant = formStore(({ variant }) => variant);
  const close = formStore(({ close }) => close);
  const perkId = formStore(({ perkId }) => perkId);
  const openEdit = formStore(({ openEdit }) => openEdit);

  const { addPerk } = useAddPerk();
  const { patchPerk } = usePatchPerk();
  const { saveDraft, isPending: isPendingDraft } = useSaveDraft();

  const { record, isLoading } = usePerk(perkId);

  const patchStep = (payload: DeepPartial<PerkFormSchema>) => {
    setDraftFormPayload((prev) => ({ ...prev, ...payload }));
  };

  useEffect(() => {
    if (!record) return;
    const logo = record.logo;
    const banner = record.banner;

    const regionsApplicable = record?.regionsApplicable || [];

    setFormPayload({
      stepOne: {
        applicableRegions: regionsApplicable.map((iso2) => ({
          iso2,
          name: regionTranslationKey[iso2 as RegionGroup] || countryIso2TranslationKey[iso2],
          type: RegionType.COUNTRY,
        })),
        companyName: record.partnerName || undefined,
        contactPerson: record.contactPerson || undefined,
        industry: record.industry || undefined,
        url: record.url || undefined,
      },
      stepTwo: {
        companyDescription: record.partnerDescription || undefined,
        companySlogan: record.partnerSlogan || undefined,
        companyShortDescription: record.partnerShortDescription || undefined,
      },
      stepThree: {
        offerDescription: record.offerDescription || undefined,
      },
      stepFour: {
        claim:
          record.claimType === PerkClaimType.PROMO_CODE
            ? {
                type: record.claimType || undefined,
                code: record.claimCode || undefined,
                displayRule: record.claimDisplayRule || undefined,
              }
            : {
                type: record.claimType || undefined,
                url: record.claimUrl || undefined,
              },
        offerTypes: record.offerTypes || [],
      },
      stepFive: {
        logo: logo
          ? {
              abort: () => '',
              loadProgress: 100,
              doc: {
                name: getS3FileOriginalName(logo),
                size: 10,
                type: 'image/png',
              },
              docLink: logo,
              id: logo,
            }
          : undefined,
        banner: banner
          ? {
              abort: () => '',
              loadProgress: 100,
              doc: {
                name: getS3FileOriginalName(banner) || '',
                size: 10,
                type: 'image/png',
              },
              docLink: banner,
              id: banner,
            }
          : undefined,
      },
    });
  }, [record, setFormPayload]);

  const isFormHasData = (data: object | PerkFormSchema): data is PerkFormSchema =>
    'stepOne' in data;

  const transformFormDataToHookFormat = <CreationMode extends boolean = false>(
    data: object | PerkFormSchema,
  ) => {
    if (!isFormHasData(data))
      return {} as CreationMode extends true ? AddPerkPayload : DraftPayload;

    const { stepFive, stepFour, stepOne, stepThree, stepTwo } = data;
    const claim: AddPerkPayload['claim'] | undefined = stepFour?.claim.type
      ? stepFour?.claim.type === PerkClaimType.PROMO_CODE
        ? {
            type: stepFour.claim.type,
            code: stepFour.claim.code,
            displayRule: stepFour.claim.displayRule,
          }
        : { url: stepFour.claim.url, type: stepFour.claim.type }
      : undefined;

    const payload = {
      ...stepOne,
      ...stepTwo,
      ...stepThree,
      ...stepFour,
      ...stepFive,
      claim,
      banner: stepFive?.banner?.docLink,
      logo: stepFive?.logo?.docLink,
      applicableRegions: stepOne?.applicableRegions?.map(({ iso2 }) => iso2),
    } satisfies DraftPayload;

    return payload as unknown as CreationMode extends true ? AddPerkPayload : DraftPayload;
  };

  const handleSubmit = (data = formPayload) => {
    switch (mode) {
      case PerkFormMode.CREATE:
        addPerk({ data: transformFormDataToHookFormat<true>(data) }, { onSuccess: close });
        break;

      case PerkFormMode.EDIT:
        if (!perkId || variant !== PerkFormVariant.FORM_ONLY) break;

        patchPerk({ data: transformFormDataToHookFormat(data), id: perkId }, { onSuccess: close });
        break;
    }
    close();
  };

  const handleNextStep = (data?: PerkFormSchema) => {
    if (mode === PerkFormMode.EDIT || mode === PerkFormMode.VIEW) return handleSubmit(data);

    const nextStep = currentStep + 1;
    if (nextStep === fromStepComponents.length) {
      return handleSubmit(data);
    }
    if (nextStep > fromStepComponents.length) return;

    setCurrentStep(nextStep);
  };

  const handleSubFormSubmit = <TStep extends keyof PerkFormSchema>(
    step: TStep,
    payload: PerkFormSchema[TStep],
  ) => {
    setFormPayload((prev) => {
      const nextPayload = (prev
        ? Object.assign(prev, { [step]: payload })
        : { [step]: payload }) as unknown as PerkFormSchema;

      handleNextStep(nextPayload);

      return nextPayload;
    });
  };

  const handlePrevStep = () => {
    const prevStep = currentStep - 1;
    if (prevStep < 0) return;
    setCurrentStep(prevStep);
  };

  const previewEditIcon = (formStep: number) =>
    variant === PerkFormVariant.READ_WITH_EDIT && (
      <EditIcon
        className="cursor-pointer"
        iconColor="#2565C8"
        onClick={() => openEdit(perkId, formStep)}
      />
    );

  const progress = ((currentStep + 1) / fromStepComponents.length) * 100;

  if (isLoading) return <Loader />;

  return (
    <>
      <div className="flex w-full items-center justify-between bg-white">
        <div className="flex flex-col gap-1">
          <span className="text-xl font-semibold text-gray-700">
            {perksFormModeTranslationKey[mode]}
          </span>
          {record?.updatedAt && mode !== PerkFormMode.CREATE && (
            <span className="text-xs font-[450] text-gray-500">
              Last edited {format(record.updatedAt, 'dd/MM/yyyy hh:mm aaa')}
            </span>
          )}
        </div>
        {mode === PerkFormMode.CREATE ? (
          <Button
            className="h-9 w-fit border border-gray-300 bg-gray-25 px-4 text-gray-700"
            disabled={!isFormHasData(draftFormPayload)}
            isLoading={isPendingDraft}
            onClick={() =>
              saveDraft(
                { data: transformFormDataToHookFormat(draftFormPayload) },
                { onSettled: close },
              )
            }
            role="button"
          >
            Save as Draft
          </Button>
        ) : (
          <Button
            className="size-9 rounded-full bg-transparent hover:bg-gray-50"
            onClick={() => {
              close();
            }}
          >
            <CrossIcon />
          </Button>
        )}
      </div>

      {mode === PerkFormMode.CREATE && (
        <div className="flex w-full items-center gap-4 bg-white">
          <div className="relative flex h-1 w-full overflow-hidden rounded bg-gray-100">
            <div
              className="absolute h-full bg-gradient-to-r from-forest-500 to-forest-300 transition-all duration-700"
              style={{ width: `${progress}%` }}
            />
          </div>
          <span className="text-label-md text-forest-500">
            {currentStep + 1}/{fromStepComponents.length}
          </span>
        </div>
      )}

      <div className="flex h-full w-full flex-col overflow-hidden bg-white pb-4">
        <div className="flex h-full w-full flex-col gap-5 overflow-y-auto rounded-b-lg bg-white">
          {mode === PerkFormMode.CREATE ||
          !record ||
          (mode === PerkFormMode.EDIT && variant === PerkFormVariant.FORM_ONLY) ? (
            fromStepComponents[currentStep]({
              onSubmit: handleSubFormSubmit,
              handlePrevStep,
              defaultData: formPayload,
              patchStep,
            })
          ) : (
            <>
              <SummaryList
                content={[
                  {
                    title: 'Company Name',
                    value: record.partnerName ? (
                      <Tooltip content={record.partnerName}>{record.partnerName}</Tooltip>
                    ) : (
                      '-'
                    ),
                  },
                  {
                    title: 'Industry',
                    value: record.industry ? companyIndustryTranslation[record.industry] : '-',
                  },
                  {
                    title: 'Contact Person',
                    value: record.contactPerson ? (
                      <Tooltip content={record.contactPerson}>{record.contactPerson}</Tooltip>
                    ) : (
                      '-'
                    ),
                  },
                  {
                    title: (
                      <div
                        className={twMerge(
                          'flex gap-1',
                          record.regionsApplicable?.length && 'flex-col',
                        )}
                      >
                        <span>Regions</span>
                        <span>Applicable:</span>
                      </div>
                    ),
                    semicolon: false,
                    value: record.regionsApplicable?.length ? (
                      <div className="flex flex-wrap items-center gap-2">
                        {record.regionsApplicable?.map((iso2) => (
                          <div
                            className="flex w-fit items-center gap-3 rounded-3xl bg-brand-50 p-[3px] px-3"
                            key={iso2}
                          >
                            <Flag countryCode={iso2.toUpperCase() as FlagCode} />
                            <span className="max-w-[220px] truncate text-sm text-gray-700">
                              {regionTranslationKey[iso2 as RegionGroup] ||
                                countryIso2TranslationKey[iso2]}
                            </span>
                          </div>
                        ))}
                      </div>
                    ) : (
                      '-'
                    ),
                  },
                ]}
                icon={previewEditIcon(0)}
                name="General"
              />
              <SummaryList
                content={[
                  {
                    title: 'Slogan',
                    value: record.partnerSlogan ? (
                      <Tooltip content={record.partnerSlogan}>{record.partnerSlogan}</Tooltip>
                    ) : (
                      '-'
                    ),
                  },
                  {
                    title: 'Short Description',
                    value: record.partnerShortDescription ? (
                      <Tooltip content={record.partnerShortDescription}>
                        {record.partnerShortDescription}
                      </Tooltip>
                    ) : (
                      '-'
                    ),
                  },
                  {
                    title: 'Description',
                    value: record.partnerDescription ? (
                      <TextEditor className="text-xs" disabled value={record.partnerDescription} />
                    ) : (
                      '-'
                    ),
                  },
                ]}
                icon={previewEditIcon(1)}
                name="Company"
              />
              <SummaryList
                content={[
                  {
                    title: 'Offer Description',
                    value: record.offerDescription ? (
                      <TextEditor className="text-xs" disabled value={record.offerDescription} />
                    ) : (
                      '-'
                    ),
                  },
                ]}
                icon={previewEditIcon(2)}
                name="Offer"
              />
              <SummaryList
                content={[
                  {
                    title: 'Type',
                    value: record.claimType ? perkClaimTypeTranslationKey[record.claimType] : '-',
                  },
                  {
                    title: 'Offer Types',
                    value: record.offerTypes?.length ? (
                      <div className="flex items-center gap-3">
                        {record.offerTypes?.map((type) => (
                          <span>{perkOfferTranslationKey[type]}</span>
                        ))}
                      </div>
                    ) : (
                      '-'
                    ),
                  },
                ]}
                icon={previewEditIcon(3)}
                name="Claim"
              />
              <SummaryList
                content={[
                  { title: 'Logo', value: record.logo ? getS3FileOriginalName(record.logo) : '-' },
                  {
                    title: 'Banner',
                    value: record.banner ? getS3FileOriginalName(record.banner) : '-',
                  },
                ]}
                icon={previewEditIcon(4)}
                name="Uploads"
              />
            </>
          )}
        </div>
      </div>
    </>
  );
};
